import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, setEntity, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { Response } from '../../shared/models/response.model';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { CertificationService } from '../certification.service';
import { Certification } from '../models/certification';
import { CroisementÉtat } from '../models/croisement-état';
import { CertificationState } from './certification.state';
import { configCertification } from './certification.store';

export function avecMéthodesCertifications<_>() {
  return signalStoreFeature(
    {
      state: type<CertificationState & EntityState<Certification> & SelectedEntityState>(),
      props: type<EntityProps<Certification>>(),
    },
    withMethods((store) => ({
      chargerCertifications: (state: { certifications: Certification[] }) =>
        patchState(store, setAllEntities(state.certifications, configCertification)),
      ajouterCertification: (state: { certification: Certification }) =>
        patchState(store, setEntity(state.certification, configCertification)),
      modifierCertification: (state: { certification: Certification }) =>
        patchState(
          store,
          updateEntity({ id: state.certification.codeFiche, changes: state.certification }, configCertification),
        ),
      selectionerCertification: (state: { codeFiche: string }) =>
        patchState(store, { selectedEntityId: state.codeFiche }),
      deselectionerCertification: () => patchState(store, { selectedEntityId: null }),
    })),
    withMethods((store, service = inject(CertificationService)) => ({
      parCodeFiche: rxMethod<{ codeFiche: string; idReferentiel: string }>(
        pipe(
          switchMap(({ codeFiche, idReferentiel }) =>
            service.parCodeFiche(codeFiche, idReferentiel).pipe(
              tapResponse({
                next: (result: Certification) => {
                  store.ajouterCertification({ certification: result });
                  store.selectionerCertification({ codeFiche: result.codeFiche });
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération de la certification, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      charger: rxMethod<{ recherche?: string; idReferentiel?: string; niveau?: number | null }>(
        pipe(
          switchMap(({ recherche, idReferentiel, niveau }) =>
            service.findAll(recherche, idReferentiel, niveau).pipe(
              tapResponse({
                next: (result: Response<Certification>) => {
                  patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                  store.chargerCertifications({
                    certifications: result.items,
                  });
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des certifications, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      modifierCroisement: rxMethod<{
        idCertification: string;
        état: CroisementÉtat;
        idRéférentiel: string;
        next: () => void;
      }>(
        pipe(
          switchMap(({ idCertification, état, idRéférentiel, next }) =>
            service.modifierCroisement(idCertification, { etatCroisement: état }, idRéférentiel).pipe(
              map(() => {
                const certification = store.entities().find((c) => c.id === idCertification)!;
                certification.etatCroisement = état;
                return certification;
              }),
              tapResponse({
                next: (result: Certification) => {
                  store.modifierCertification({ certification: result });
                  next();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification du croisement, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      modifierTrigrammeType: rxMethod<{
        idCertification: string;
        trigrammeType: string;
      }>(
        pipe(
          switchMap(({ idCertification, trigrammeType }) =>
            service.modifier(idCertification, { trigrammeType: trigrammeType }).pipe(
              map(() => {
                const certification = store.entities().find((c) => c.id === idCertification)!;
                certification.trigrammeType = trigrammeType;
                return certification;
              }),
              tapResponse({
                next: (result: Certification) => {
                  store.modifierCertification({ certification: result });
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification de la certification, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
